import { useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * URL search params 单 key 双向绑定。
 *
 * <p>替换散落的 `const next = new URLSearchParams(searchParams); next.set(k, v); setSearchParams(next);`
 * 模板代码(audit 报告: 至少 5 个页面手写)。
 *
 * <p>用法:
 * ```ts
 * const [tenantId, setTenantId] = useUrlParam("tenantId");
 * setTenantId("123");   // ?tenantId=123
 * setTenantId(null);    // 删 key
 * ```
 *
 * @returns `[value, setValue]`,value 为 string | null。
 */
export function useUrlParam(key: string): [string | null, (v: string | null) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  // 每次渲染同步最新 params 到 ref;update 从 ref 累积推进 → 同一 handler 里多次 set 叠加不互相覆盖。
  const ref = useRef(searchParams);
  ref.current = searchParams;
  const value = searchParams.get(key);

  const setValue = (v: string | null) => {
    const next = new URLSearchParams(ref.current);
    if (v === null || v === "") {
      next.delete(key);
    } else {
      next.set(key, v);
    }
    ref.current = next;
    setSearchParams(next);
  };

  return [value, setValue];
}

/**
 * URL search params 多 key 批量更新。
 *
 * <p>用法:
 * ```ts
 * const { sp, update } = useUrlParams();
 * const page = Number(sp.get("page") ?? "1");
 * update({ page: "2", search: "hello" });             // ?page=2&search=hello (push)
 * update({ search: null });                           // 删 search,保留 page
 * update({ q: "h" }, { replace: true });              // 按键搜索这种高频更新用 replace
 *                                                     // 不污染 history。
 * ```
 *
 * <p>V40 (2026-05-19) — 加 ``replace`` 选项 (默认 push),用于按键搜索 / 输入框 onChange 这种
 * 高频更新场景,避免浏览器历史里堆 100 个中间态。
 */
export function useUrlParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  // V-#9 (2026-07-04) — 根治 bug:旧版从闭包 `searchParams` 快照建 next,同一 handler 里两次 update() 都读同一
  // stale 快照 → 第二次覆盖第一次(实证:切「归属筛选」+ 重置页码两次 update 互相抹掉 mine/scope)。
  // react-router `setSearchParams(fn)` 同步两次也不链式(各读同一 ref)→ 改用「每渲染同步 ref + update 累积推进
  // ref」:第一次 update 推进 ref,第二次读到累积值,两次叠加。外部导航改 params 时下次渲染 resync ref。
  const ref = useRef(searchParams);
  ref.current = searchParams;

  const update = (
    patch: Record<string, string | number | null | undefined>,
    options?: { replace?: boolean }
  ) => {
    const next = new URLSearchParams(ref.current);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === null || v === "") {
        next.delete(k);
      } else {
        next.set(k, String(v));
      }
    }
    ref.current = next;
    setSearchParams(next, { replace: options?.replace ?? false });
  };

  return { sp: searchParams, update };
}
